import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Alert, Button, Input, Field, Loader, Modal, Table } from '@itds/elements/index.js';
import { Colors, FontSizes } from '@itds/Theme';

import { Api } from '../../../../utils/Api';

const AgeLimitsList = ({ version }) => {
  const [ageLimits, setAgeLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedAgeLimits, setSelectedAgeLimits] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [editingAgeLimit, setEditingAgeLimit] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    // Based on actual API response structure
    minimum_annuitant_age_qualified: '',
    minimum_annuitant_age_non_qualified: '',
    maximum_annuitant_age_qualified: '',
    maximum_annuitant_age_non_qualified: '',
    minimum_owners_age_qualified: '',
    minimum_owners_age_non_qualified: '',
    maximum_owners_age_qualified: '',
    maximum_owners_age_non_qualified: ''
  });

  useEffect(() => {
    if (version?.id) {
      loadAgeLimits();
    }
  }, [version]);

  const loadAgeLimits = async action => {
    try {
      setLoading(true);

      setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 3000);

      const response = await Api.getAges(version.id);
      const ageLimitData = response.data || [];

      setAgeLimits(ageLimitData);

      if (action === 'add') {
        setSuccessMessage('Age Limit Successfully Added');
      } else if (action === 'update') {
        setSuccessMessage('Age Limit Successfully Updated');
      } else if (action === 'delete') {
        setSuccessMessage('Age Limit(s) Successfully Deleted');
      }
    } catch (err) {
      console.error('Error loading age limits:', err);
      if (action === 'add') {
        setErrorMessage('Unable to add Age Limit at this time');
      } else if (action === 'update') {
        setErrorMessage('Unable to update Age Limit at this time');
      } else if (action === 'delete') {
        setErrorMessage('Unable to delete Age Limit(s) at this time');
      } else {
        setErrorMessage('Failed to load age limits');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      minimum_annuitant_age_qualified: '',
      minimum_annuitant_age_non_qualified: '',
      maximum_annuitant_age_qualified: '',
      maximum_annuitant_age_non_qualified: '',
      minimum_owners_age_qualified: '',
      minimum_owners_age_non_qualified: '',
      maximum_owners_age_qualified: '',
      maximum_owners_age_non_qualified: ''
    });
  };

  const handleSelectAll = e => {
    e.stopPropagation();

    if (ageLimits.length === selectedAgeLimits.length) {
      setSelectedAgeLimits([]);
    } else {
      setSelectedAgeLimits(ageLimits.map(a => a.id));
    }
  };

  const handleAgeLimitSelectChange = (ageLimit, e) => {
    e.stopPropagation();

    const selectedIds = selectedAgeLimits.filter(id => id !== ageLimit.id);

    if (!selectedAgeLimits.includes(ageLimit.id)) {
      selectedIds.push(ageLimit.id);
    }

    setSelectedAgeLimits(selectedIds);
  };

  const handleCreate = () => {
    resetForm();
    setEditingAgeLimit(null);
    setShowCreateModal(true);
  };

  const handleEdit = (ageLimit, e) => {
    if (e) e.stopPropagation();

    setEditingAgeLimit(ageLimit);
    setFormData({
      minimum_annuitant_age_qualified: ageLimit.minimum_annuitant_age_qualified || '',
      minimum_annuitant_age_non_qualified: ageLimit.minimum_annuitant_age_non_qualified || '',
      maximum_annuitant_age_qualified: ageLimit.maximum_annuitant_age_qualified || '',
      maximum_annuitant_age_non_qualified: ageLimit.maximum_annuitant_age_non_qualified || '',
      minimum_owners_age_qualified: ageLimit.minimum_owners_age_qualified || '',
      minimum_owners_age_non_qualified: ageLimit.minimum_owners_age_non_qualified || '',
      maximum_owners_age_qualified: ageLimit.maximum_owners_age_qualified || '',
      maximum_owners_age_non_qualified: ageLimit.maximum_owners_age_non_qualified || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = (ageLimit, e) => {
    e.stopPropagation();
    setSelectedAgeLimits([ageLimit.id]);
    setShowBulkDelete(true);
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      setShowBulkDelete(false);

      await Promise.all(selectedAgeLimits.map(id => Api.deleteAge(id)));

      setSelectedAgeLimits([]);
      loadAgeLimits('delete');
    } catch (err) {
      console.error('Error deleting age limits:', err);
      setErrorMessage('Failed to delete age limit(s)');
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Validate age limits (0-99)
    const numValue = parseInt(value, 10);

    if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 99)) {
      return; // Don't update if invalid
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        product_id: parseInt(version.id, 10),
        beacon_id: version.beacon_id || 0,
        version_id: version.version_id || 0,
        ...formData
      };

      // Convert string values to numbers where applicable
      Object.keys(payload).forEach(key => {
        if (key.includes('minimum') || key.includes('maximum')) {
          if (payload[key] !== '') {
            payload[key] = parseInt(payload[key], 10) || null;
          } else {
            payload[key] = null;
          }
        }
      });

      let response;

      if (editingAgeLimit) {
        response = await Api.updateAge(editingAgeLimit.id, payload);
        loadAgeLimits('update');
      } else {
        response = await Api.postAge(payload);
        loadAgeLimits('add');
      }

      if (response) {
        setShowCreateModal(false);
        setShowEditModal(false);
        setEditingAgeLimit(null);
        resetForm();
      }
    } catch (err) {
      console.error('Error saving age limit:', err);
      setErrorMessage(`Failed to ${editingAgeLimit ? 'update' : 'create'} age limit`);
    } finally {
      setSaving(false);
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingAgeLimit(null);
    resetForm();
  };

  const handleRowClick = ageLimit => {
    handleEdit(ageLimit);
  };

  const formatAge = value => {
    if (value === null || typeof value === 'undefined') return '-';
    return value.toString();
  };

  const renderModal = () => {
    const isEdit = !!editingAgeLimit;
    const isOpen = showCreateModal || showEditModal;
    const title = isEdit ? 'Edit Age Limit' : 'Create New Age Limit';

    if (!isOpen) return null;

    return (
      <Modal
        buttons={[
          {
            onClick: handleModalClose,
            children: 'Cancel',
            style: { marginRight: '10px' }
          },
          {
            onClick: handleSave,
            children: (() => {
              if (saving) return 'Saving...';
              return isEdit ? 'Update' : 'Create';
            })(),
            disabled: saving,
            color: 'PRIMARY'
          }
        ]}
        maxWidth={600}
        onClose={handleModalClose}
        title={title}
      >
        <div style={{ padding: '20px', minWidth: '600px' }}>
          {/* Annuitant Ages Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>Annuitant Ages</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <Field label='Minimum Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('minimum_annuitant_age_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.minimum_annuitant_age_qualified}
                  />
                </Field>
              </div>
              <div>
                <Field label='Maximum Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('maximum_annuitant_age_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.maximum_annuitant_age_qualified}
                  />
                </Field>
              </div>
              <div>
                <Field label='Minimum Non-Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('minimum_annuitant_age_non_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.minimum_annuitant_age_non_qualified}
                  />
                </Field>
              </div>
              <div>
                <Field label='Maximum Non-Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('maximum_annuitant_age_non_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.maximum_annuitant_age_non_qualified}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Owner Ages Section */}
          <div>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>Owner Ages</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <Field label='Minimum Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('minimum_owners_age_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.minimum_owners_age_qualified}
                  />
                </Field>
              </div>
              <div>
                <Field label='Maximum Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('maximum_owners_age_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.maximum_owners_age_qualified}
                  />
                </Field>
              </div>
              <div>
                <Field label='Minimum Non-Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('minimum_owners_age_non_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.minimum_owners_age_non_qualified}
                  />
                </Field>
              </div>
              <div>
                <Field label='Maximum Non-Qualified' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('maximum_owners_age_non_qualified', e.target.value)
                    }
                    placeholder='Enter age (0-99)'
                    type='number'
                    value={formData.maximum_owners_age_non_qualified}
                  />
                </Field>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {successMessage && <Alert type='success'>{successMessage}</Alert>}
      {errorMessage && <Alert type='error'>{errorMessage}</Alert>}

      {renderModal()}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDelete && (
        <Modal
          buttons={[
            {
              color: 'RED',
              onClick: handleBulkDelete,
              children: 'Delete',
              style: { marginLeft: 'auto' }
            }
          ]}
          onClose={() => setShowBulkDelete(false)}
          title='Are you sure?'
        >
          <div
            style={{
              padding: 30,
              lineHeight: '30px',
              fontSize: FontSizes.LARGE,
              textAlign: 'center'
            }}
          >
            Be careful...you are about to delete {selectedAgeLimits.length} age limit record(s).
            This will remove them completely from the platform. Make sure you know what you are
            doing before completing this action.
          </div>
        </Modal>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', marginBottom: 30, justifyContent: 'flex-end' }}>
        <Button
          color='RED'
          disabled={!selectedAgeLimits.length}
          inline={true}
          onClick={() => setShowBulkDelete(true)}
          style={{ marginRight: 10 }}
        >
          Delete Selected
        </Button>
        <Button inline={true} onClick={handleCreate} style={{}}>
          Create New Age Limit
        </Button>
      </div>

      {/* Age Limits Table */}
      <div
        style={{
          fontSize: '14px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden'
        }}
      >
        <style>
          {`
            .age-limits-table th,
            .age-limits-table td {
              border-right: 1px solid #e0e0e0;
              padding: 8px 12px;
            }
            .age-limits-table th:last-child,
            .age-limits-table td:last-child {
              border-right: none;
            }
            .age-limits-table tbody tr {
              border-bottom: 1px solid #f0f0f0;
            }
            .age-limits-table tbody tr:hover {
              background-color: #f8f9fa;
            }
            .age-limits-table thead th {
              background-color: #f5f5f5;
              border-bottom: 2px solid #d0d0d0;
              font-weight: 600;
            }
          `}
        </style>
        <div className='age-limits-table'>
          <Table
            columns={[
              {
                id: 'select',
                label: (
                  <i
                    className={`mdi ${
                      ageLimits.length === selectedAgeLimits.length
                        ? 'mdi-checkbox-marked'
                        : 'mdi-checkbox-blank-outline'
                    }`}
                    onClick={handleSelectAll}
                    style={{
                      fontSize: 16,
                      cursor: 'pointer',
                      color:
                        ageLimits.length === selectedAgeLimits.length ? Colors.GREEN.hex : '#777'
                    }}
                  />
                ),
                render: ageLimit => {
                  const selected = selectedAgeLimits.includes(ageLimit.id);

                  return (
                    <i
                      className={`mdi ${
                        selected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
                      }`}
                      onClick={handleAgeLimitSelectChange.bind(null, ageLimit)}
                      style={{
                        fontSize: 16,
                        cursor: 'pointer',
                        color: selected ? Colors.GREEN.hex : '#777'
                      }}
                    />
                  );
                },
                styles: { width: '30px', minWidth: '30px' }
              },
              {
                id: 'annuitant_min_qual',
                label: <span title='Annuitant Minimum Qualified Age'>Ann-Min-Qual</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.minimum_annuitant_age_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'annuitant_max_qual',
                label: <span title='Annuitant Maximum Qualified Age'>Ann-Max-Qual</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.maximum_annuitant_age_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'annuitant_min_non_qual',
                label: <span title='Annuitant Minimum Non-Qualified Age'>Ann-Min-Non-Q</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.minimum_annuitant_age_non_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'annuitant_max_non_qual',
                label: <span title='Annuitant Maximum Non-Qualified Age'>Ann-Max-Non-Q</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.maximum_annuitant_age_non_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'owner_min_qual',
                label: <span title='Owner Minimum Qualified Age'>Own-Min-Qual</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.minimum_owners_age_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'owner_max_qual',
                label: <span title='Owner Maximum Qualified Age'>Own-Max-Qual</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.maximum_owners_age_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'owner_min_non_qual',
                label: <span title='Owner Minimum Non-Qualified Age'>Own-Min-Non-Q</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.minimum_owners_age_non_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'owner_max_non_qual',
                label: <span title='Owner Maximum Non-Qualified Age'>Own-Max-Non-Q</span>,
                is_sortable: true,
                render: ageLimit => formatAge(ageLimit.maximum_owners_age_non_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'updated_at',
                label: 'Updated',
                is_sortable: true,
                render: ageLimit => format(new Date(ageLimit.updated_at), 'MM/dd/yy'),
                styles: { fontSize: '15px', whiteSpace: 'nowrap', width: '70px' }
              },
              {
                id: 'actions',
                label: '',
                render: ageLimit => {
                  return (
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Button
                        color='RED'
                        inline={true}
                        onClick={handleDelete.bind(null, ageLimit)}
                        small={true}
                        style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          display: 'inline-flex',
                          justifyContent: 'center'
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  );
                },
                styles: { textAlign: 'right', width: '80px', minWidth: '80px' }
              }
            ]}
            data={ageLimits}
            defaultSortColumn={{
              id: 'updated_at',
              label: 'Updated',
              is_sortable: true
            }}
            onRowClick={handleRowClick}
            perPage={20}
            placeholder='No age limits found'
          />
        </div>
      </div>
    </>
  );
};

AgeLimitsList.propTypes = {
  version: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    beacon_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    version_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    product_type: PropTypes.string,
    product_type_detail: PropTypes.string
  })
};

export default AgeLimitsList;
